// ============================================================================
// STATISTIQUES › STOCK — un univers à cinq onglets (Résumé · Reporting stock · Taux de sortie · Stock par périodes · Stock
// par tailles) qui partagent leur barre de réglages. Réf. maquette du back-office (écran `skr`, « UNIVERS STATISTIQUES ·
// STOCK ») et docs/CADRAGE-Statistiques-Stock.md.
//
// ⚠️ L'ÉCRAN DESSINE, IL NE DÉFINIT AUCUN NOMBRE : le stock à une date, sa valeur au PMP rejoué, les flux, la couverture,
//    le taux de sortie, la rotation et la faible sortie viennent du serveur (`sales/stats-stock.ts`). Ce qui se calcule
//    ici est de la GÉOMÉTRIE (positions, tracés, échelles).
// ⚠️ UN INDICATEUR NON MESURABLE S'AFFICHE « — » ET DIT POURQUOI, JAMAIS ZÉRO : une valeur dont une déclinaison en stock
//    n'a pas de coût, une couverture sans vente.
// ⚠️ LES ONGLETS SONT DES VUES DU MÊME ÉCRAN : l'onglet ouvert vit dans l'adresse (`?onglet=`), et chacun ne lit que
//    SES chiffres, seulement quand il est ouvert ; la barre de réglages est partagée.
// ============================================================================
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, Icon, Tabs, Trans, useChartTip } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import {
  api, type SalesSummaryAxis, type SalesSummaryFilterKey, type StockAge, type StockCoverageRow, type StockDormantRow, type StockPace,
  type StockExitView, type StockPeriodGrain, type StockPeriodsView, type StockReportView, type StockSizesView, type StockSellThroughRow, type StockSizeCurve, type StockSummaryBucket, type StockSummaryView,
  type StatsViewSettings,
} from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';
import { useStoreScope } from '../store-context';
import { useAncreDuJour } from '../periode';
import { exporterCsv } from './export-csv';
import { FILTER_DIMS, StatsBar, StatsLinesRow, defaultSettings, isoOf, rangeLabel, rangeOf, seasonName, type StatsSettings } from './stats-bar';
import { csvRate, eur0, pc, shownValue } from './stats-format';
import {
  AXES, CARD, CARD_ROW, CardHead, EMPTY, KPI_PAD_X, KPI_ROW, KPI_TILE, KpiCard, KpiLabel, MONO_LABEL, Seg, Shimmer, TONES, axisLabel, bucketLabels, isoOfYmd,
  monotonePath, niceTop, type Tone,
} from './stats-resume';
import { fieldLabel } from '../vocabulary';
import { ReadFailed, asOfNote, reportScope } from './stats-ventes';
import { ReportDisplayToggle, type ReportDisplay } from './stats-reporting';
import { STOCK_REPORT_DEFAULT_SORT, STOCK_REPORT_SORT_KEYS, StockReportTable, exportStockReport, stockReportLegend } from './stats-stock-reporting';
import {
  RetroModeToggle, STOCK_EXIT_DEFAULT_SORT, STOCK_EXIT_SORT_KEYS, StockExitTable, exportStockExit, stockExitLegend, type RetroMode,
} from './stats-stock-exit';
import { STOCK_PERIODS_DEFAULT_SORT, StockPeriodsControls, StockPeriodsTable, exportStockPeriods } from './stats-stock-periods';
import {
  DEFAULT_SIZE_MEASURES, SIZE_MEASURES, STOCK_SIZES_DEFAULT_SORT, StockSizesControls, StockSizesTable, exportStockSizes, type SizeMeasure,
} from './stats-stock-sizes';
import type { StatsSort } from './stats-grid';
import { StatsViewsButton, describeView, keptSort, type StatsViewAdapter } from './stats-views';

type TabId = 'resume' | 'reporting' | 'sortie' | 'periodes' | 'tailles';
const TABS: { id: TabId; label: Translatable; icon: IconName }[] = [
  { id: 'resume', label: key('Résumé'), icon: 'dashboard' },
  { id: 'reporting', label: key('Reporting stock'), icon: 'table' },
  { id: 'sortie', label: key('Taux de sortie'), icon: 'trendUp' },
  { id: 'periodes', label: key('Stock par périodes'), icon: 'calendar' },
  { id: 'tailles', label: key('Stock par tailles'), icon: 'sizeRuler' },
];

type Phase = { phase: 'idle' } | { phase: 'loading' } | { phase: 'ready' } | { phase: 'failed'; message: string };

/** `AAAA-MM-JJ` → `AAAAMMJJ`, la forme que le serveur lit. */
const ymd = (iso: string): string => iso.replace(/-/g, '');

/** Le surtitre de l'univers : « Statistiques · Stock ». */
const EYEBROW: CSSProperties = {
  display: 'block', marginBottom: 6, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', letterSpacing: '.09em', textTransform: 'uppercase',
  color: 'var(--ink-500)',
};

/** Le bouton « Export Excel » des statistiques — inactif tant qu'il n'y a rien de lu, ou pendant une lecture. */
const exportButton = (on: boolean): CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
  whiteSpace: 'nowrap', cursor: on ? 'pointer' : 'default', opacity: on ? 1 : 0.55,
});

export function StatsStock() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((x) => x.id === params.get('onglet'))?.id ?? 'resume') as TabId;
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const ancre = useAncreDuJour();
  const today = isoOf(ancre);
  const [settings, setSettings] = useState<StatsSettings>(() => defaultSettings(ancre));
  const [view, setView] = useState<StockSummaryView | null>(null);
  // L'axe de « Combien de jours de vente en stock » vit ici : l'export emporte celui que l'écran montre.
  const [coverageAxis, setCoverageAxis] = useState<SalesSummaryAxis>('fam');
  // La grille de « La courbe des tailles » aussi ; `null` = la plus stockée, celle que le serveur range en premier.
  const [sizeGrid, setSizeGrid] = useState<string | null>(null);
  const [status, setStatus] = useState<Phase>({ phase: 'idle' });
  const shown = useRef(false);
  const busy = status.phase === 'loading';
  // Reporting stock : ses niveaux, son affichage et son tri survivent au passage d'un onglet à l'autre.
  const [axes, setAxes] = useState<SalesSummaryFilterKey[]>(['sup']);
  const [display, setDisplay] = useState<ReportDisplay>('a');
  const [sort, setSort] = useState<StatsSort>(STOCK_REPORT_DEFAULT_SORT);
  const [report, setReport] = useState<StockReportView | null>(null);
  const [reportStatus, setReportStatus] = useState<Phase>({ phase: 'idle' });
  const reportShown = useRef(false);
  const reportBusy = reportStatus.phase === 'loading';
  // Reporting taux de sortie : ses niveaux (le fournisseur, comme le dessin), son affichage, son tri et ses rétrocessions.
  const [exitAxes, setExitAxes] = useState<SalesSummaryFilterKey[]>(['sup']);
  const [exitDisplay, setExitDisplay] = useState<ReportDisplay>('a');
  const [exitSort, setExitSort] = useState<StatsSort>(STOCK_EXIT_DEFAULT_SORT);
  const [retro, setRetro] = useState<RetroMode>('in');
  const [exitReport, setExitReport] = useState<StockExitView | null>(null);
  const [exitStatus, setExitStatus] = useState<Phase>({ phase: 'idle' });
  const exitShown = useRef(false);
  const exitBusy = exitStatus.phase === 'loading';
  // Stock par périodes : ses niveaux, son grain (le mois, comme le dessin) et son tri.
  const [periodAxes, setPeriodAxes] = useState<SalesSummaryFilterKey[]>(['sup']);
  const [grain, setGrain] = useState<StockPeriodGrain>('m');
  const [periodSort, setPeriodSort] = useState<StatsSort>(STOCK_PERIODS_DEFAULT_SORT);
  const [periods, setPeriods] = useState<StockPeriodsView | null>(null);
  const [periodStatus, setPeriodStatus] = useState<Phase>({ phase: 'idle' });
  const periodShown = useRef(false);
  const periodBusy = periodStatus.phase === 'loading';
  // Stock par tailles : ses niveaux, sa grille (`null` = la plus stockée, choisie par le serveur), ses mesures et son tri.
  const [sizeAxes, setSizeAxes] = useState<SalesSummaryFilterKey[]>(['sup']);
  const [gridChoice, setGridChoice] = useState<string | null>(null);
  const [sizeMeasures, setSizeMeasures] = useState<SizeMeasure[]>(DEFAULT_SIZE_MEASURES);
  const [sizeSort, setSizeSort] = useState<StatsSort>(STOCK_SIZES_DEFAULT_SORT);
  const [sizeView, setSizeView] = useState<StockSizesView | null>(null);
  const [sizeStatus, setSizeStatus] = useState<Phase>({ phase: 'idle' });
  const sizeShown = useRef(false);
  const sizeBusy = sizeStatus.phase === 'loading';

  /**
   * LES RÉGLAGES DE LA BARRE. Choisir une comparaison montre l'ÉCART, l'arrêter revient à la période (maquette) : c'est
   * la comparaison que l'on vient de demander qu'on veut lire.
   */
  const changeSettings = (next: StatsSettings) => {
    if (settings.b === null && next.b !== null) { setDisplay('d'); setExitDisplay('d'); }
    if (next.b === null) { setDisplay('a'); setExitDisplay('a'); }
    setSettings(next);
  };
  /** Redessiner les niveaux rend l'ordre de la ventilation : un tri manuel ne décrit plus le tableau affiché. */
  const changeAxes = (next: SalesSummaryFilterKey[]) => { setAxes(next); setSort(STOCK_REPORT_DEFAULT_SORT); };
  const changeExitAxes = (next: SalesSummaryFilterKey[]) => { setExitAxes(next); setExitSort(STOCK_EXIT_DEFAULT_SORT); };
  const changePeriodAxes = (next: SalesSummaryFilterKey[]) => { setPeriodAxes(next); setPeriodSort(STOCK_PERIODS_DEFAULT_SORT); };
  /** Un tri posé sur une période qui disparaît avec le grain n'a plus de sens : on rend l'ordre de la ventilation. */
  const changeGrain = (next: StockPeriodGrain) => { setGrain(next); setPeriodSort(STOCK_PERIODS_DEFAULT_SORT); };
  const changeSizeAxes = (next: SalesSummaryFilterKey[]) => { setSizeAxes(next); setSizeSort(STOCK_SIZES_DEFAULT_SORT); };
  /** Une autre grille, d'autres colonnes : un tri posé sur une taille n'a plus de sens. */
  const changeGrid = (next: string) => { setGridChoice(next); setSizeSort(STOCK_SIZES_DEFAULT_SORT); };

  const a = rangeOf(settings.a);
  const b = settings.b ? rangeOf(settings.b) : null;
  const request = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
    q: settings.q.trim(), filters: settings.filters,
  }), [a.from, a.to, b?.from, b?.to, settings.q, settings.filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // ⚠️ AUCUNE REQUÊTE SANS MAGASIN NI HORS DU RÉSUMÉ : un onglet fermé ne lit rien.
    if (!gln || tab !== 'resume' || !request.from || !request.to) { setStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ LE VOILE TOMBE TOUT DE SUITE, L'APPEL ATTEND LA FIN DE LA FRAPPE : les chiffres d'avant ne décrivent plus les
    //    réglages affichés dès la première touche, et l'export doit être fermé pendant ce temps aussi.
    setStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.stockSummary(request)
        .then((v) => { if (!alive) return; shown.current = true; setView(v); setStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ La donnée précédente est JETÉE : garder les chiffres d'une période qu'on vient de quitter mentirait.
          setView(null);
          setStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, shown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, request]);

  const reportRequest = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
    axes, q: settings.q.trim(), filters: settings.filters,
  }), [a.from, a.to, b?.from, b?.to, axes, settings.q, settings.filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gln || tab !== 'reporting' || !reportRequest.from || !reportRequest.to) { setReportStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Mêmes règles que le Résumé : le voile tout de suite, l'appel à la fin de la frappe, la donnée jetée sur un échec.
    setReportStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.stockReport(reportRequest)
        .then((v) => { if (!alive) return; reportShown.current = true; setReport(v); setReportStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          setReport(null);
          setReportStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, reportShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, reportRequest]);

  const exitRequest = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
    axes: exitAxes, q: settings.q.trim(), filters: settings.filters,
  }), [a.from, a.to, b?.from, b?.to, exitAxes, settings.q, settings.filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gln || tab !== 'sortie' || !exitRequest.from || !exitRequest.to) { setExitStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Mêmes règles que les autres onglets : le voile tout de suite, l'appel à la fin de la frappe, la donnée jetée sur un
    //    échec. Le sélecteur des rétrocessions ne relit RIEN : les deux taux sont dans la réponse.
    setExitStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.stockExitReport(exitRequest)
        .then((v) => { if (!alive) return; exitShown.current = true; setExitReport(v); setExitStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          setExitReport(null);
          setExitStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, exitShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, exitRequest]);

  // ⚠️ AUCUNE COMPARAISON N'EST ENVOYÉE : un stock par périodes se lit sur la seule période choisie.
  const periodRequest = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), axes: periodAxes, q: settings.q.trim(), filters: settings.filters, grain,
  }), [a.from, a.to, periodAxes, settings.q, settings.filters, grain]);

  useEffect(() => {
    if (!gln || tab !== 'periodes' || !periodRequest.from || !periodRequest.to) { setPeriodStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Mêmes règles que les autres onglets : le voile tout de suite, l'appel à la fin de la frappe, la donnée jetée sur un
    //    échec.
    setPeriodStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.stockPeriods(periodRequest)
        .then((v) => { if (!alive) return; periodShown.current = true; setPeriods(v); setPeriodStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          setPeriods(null);
          setPeriodStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, periodShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, periodRequest]);

  // ⚠️ AUCUNE COMPARAISON N'EST ENVOYÉE, et la grille par défaut (`null`) est choisie par le serveur : la plus stockée.
  const sizeRequest = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), axes: sizeAxes, q: settings.q.trim(), filters: settings.filters, grid: gridChoice,
  }), [a.from, a.to, sizeAxes, settings.q, settings.filters, gridChoice]);

  useEffect(() => {
    if (!gln || tab !== 'tailles' || !sizeRequest.from || !sizeRequest.to) { setSizeStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Mêmes règles que les autres onglets : le voile tout de suite, l'appel à la fin de la frappe, la donnée jetée sur un
    //    échec. Les mesures cochées ne relisent RIEN : chaque cellule les porte toutes.
    setSizeStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.stockSizes(sizeRequest)
        .then((v) => { if (!alive) return; sizeShown.current = true; setSizeView(v); setSizeStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          setSizeView(null);
          setSizeStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, sizeShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, sizeRequest]);

  const pick = (id: TabId) => {
    const next = new URLSearchParams(params);
    if (id === 'resume') next.delete('onglet'); else next.set('onglet', id);
    setParams(next, { replace: true });
  };

  // ─── Les vues enregistrées ─────────────────────────────────────────────────────────────────────
  /** La barre telle qu'une vue la reprend ; la comparaison seulement sur un onglet qui compare. */
  const barOf = (compares: boolean) => ({ a: settings.a, ...(compares ? { b: settings.b } : {}), q: settings.q, filters: settings.filters });
  /**
   * Appliquer la barre d'une vue. ⚠️ LA BARRE EST PARTAGÉE PAR LES ONGLETS : la période et les filtres d'une vue valent pour
   * tous, comme s'ils avaient été choisis à la main. Une vue d'un onglet qui ne compare pas laisse la comparaison en place —
   * invisible ici, elle reste celle des autres onglets.
   */
  const applyBar = (v: StatsViewSettings, compares: boolean) =>
    setSettings((cur) => ({ a: v.a, b: compares ? (v.b ?? null) : cur.b, q: v.q, filters: v.filters }));
  const describe = (v: StatsViewSettings) => describeView(v, FILTER_DIMS);
  const reportViews: StatsViewAdapter = {
    snapshot: () => ({ ...barOf(true), axes, sort, display }),
    apply: (v) => {
      applyBar(v, true);
      setAxes(v.axes as SalesSummaryFilterKey[]);
      setDisplay(v.b ? (v.display ?? 'd') : 'a');
      const k = keptSort(v.sort, v.axes.length, STOCK_REPORT_SORT_KEYS, STOCK_REPORT_DEFAULT_SORT);
      setSort(k.sort);
      return k.lost;
    },
    describe,
    note: t('Axes, périodes comparées, filtres, affichage et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };
  const exitViews: StatsViewAdapter = {
    snapshot: () => ({ ...barOf(true), axes: exitAxes, sort: exitSort, display: exitDisplay, retro }),
    apply: (v) => {
      applyBar(v, true);
      setExitAxes(v.axes as SalesSummaryFilterKey[]);
      setExitDisplay(v.b ? (v.display ?? 'd') : 'a');
      if (v.retro) setRetro(v.retro);
      const k = keptSort(v.sort, v.axes.length, STOCK_EXIT_SORT_KEYS, STOCK_EXIT_DEFAULT_SORT);
      setExitSort(k.sort);
      return k.lost;
    },
    describe,
    note: t('Axes, périodes comparées, filtres, rétrocessions et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };
  const periodViews: StatsViewAdapter = {
    snapshot: () => ({ ...barOf(false), axes: periodAxes, sort: periodSort, grain }),
    apply: (v) => {
      applyBar(v, false);
      setPeriodAxes(v.axes as SalesSummaryFilterKey[]);
      if (v.grain) setGrain(v.grain);
      // ⚠️ LES COLONNES SONT DES PÉRIODES : elles ne se connaissent qu'une fois lues, le tri est repris tel quel.
      const k = keptSort(v.sort, v.axes.length, null, STOCK_PERIODS_DEFAULT_SORT);
      setPeriodSort(k.sort);
      return k.lost;
    },
    describe,
    note: t('Axes, période, grain, filtres et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };
  const sizeViews: StatsViewAdapter = {
    snapshot: () => ({ ...barOf(false), axes: sizeAxes, sort: sizeSort, grid: gridChoice, measures: sizeMeasures }),
    apply: (v) => {
      applyBar(v, false);
      setSizeAxes(v.axes as SalesSummaryFilterKey[]);
      let lost = 0;
      // ⚠️ UNE GRILLE QUI N'EXISTE PLUS N'EST PAS DEMANDÉE : le serveur la refuserait, et l'onglet n'afficherait qu'un refus.
      //    On rend la plus stockée, et l'écart est compté.
      if (v.grid !== undefined) {
        const gone = v.grid !== null && !!sizeView && !sizeView.grids.some((g) => g.id === v.grid);
        if (gone) lost += 1;
        setGridChoice(gone ? null : v.grid);
      }
      if (v.measures) {
        // L'ordre de la vue ne compte pas : le tableau et l'export empilent les mesures dans l'ordre du dessin.
        const kept = v.measures.filter((m): m is SizeMeasure => (SIZE_MEASURES as readonly string[]).includes(m));
        lost += v.measures.length - kept.length;
        setSizeMeasures(kept.length ? kept : DEFAULT_SIZE_MEASURES);
      }
      // ⚠️ LES COLONNES SONT DES TAILLES : elles ne se connaissent qu'une fois lues, le tri est repris tel quel.
      const k = keptSort(v.sort, v.axes.length, null, STOCK_SIZES_DEFAULT_SORT);
      setSizeSort(k.sort);
      return lost + k.lost;
    },
    describe,
    note: t('Axes, période, grille, mesures, filtres et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };

  return (
    <>
      <div style={{ margin: '-6px 0 18px' }}>
        <span style={EYEBROW}>{t('Statistiques · Stock')}</span>
        <Tabs size="strip" label={t('Statistiques de stock')} panelId="stats-stock-panel" value={tab} onChange={pick}
          items={TABS.map((x) => ({ id: x.id, label: t(x.label), icon: x.icon }))} />
      </div>
      <div id="stats-stock-panel" role="tabpanel">
        {tab === 'resume' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Résumé du stock')}</h1>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='Ce que vaut le stock, comment il bouge et <1>où il faut agir</1> : ce qui dort, ce qui manque, ce qui vieillit.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={() => { if (view && !busy) exportStock(view, coverageAxis, currentLang(), sizeGrid); }} disabled={!view || busy}
                title={t('On exporte ce que l’on voit')} className="oz-stats-export" style={exportButton(!!view && !busy)}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <StatsBar value={settings} onChange={changeSettings} today={today} scope={view ? t('{n} article', { n: view.scope.references }) : ''}
              periodNote={asOfNote(view?.asOf ?? null, view?.period, view?.reference)}
              options={view?.filterOptions ?? {}} seasons={view?.seasonPeriods ?? []} />
            {!gln && (
              <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire son stock.')} /></div>
            )}
            {gln && status.phase === 'failed' && (
              <ReadFailed message={status.message}
                text={t('La lecture n’a pas abouti : {message}. Aucun chiffre n’est affiché — ce n’est pas un magasin sans stock, c’est une mesure qui n’a pas pu être faite.', { message: status.message })} />
            )}
            {/* La PREMIÈRE mesure : le cadre du Résumé, à sa place, sans un chiffre. Les suivantes voilent les chiffres d'avant. */}
            {gln && !view && status.phase === 'loading' && <StockResumePlaceholder />}
            {view && (
              // ⚠️ PENDANT UNE LECTURE, LES CHIFFRES D'AVANT SONT VOILÉS ET DITS : ils ne sont plus ceux des réglages affichés.
              <div aria-busy={busy} style={{ opacity: busy ? 0.55 : 1, transition: 'opacity .15s' }}>
                <StockResume view={view} lang={currentLang()} axis={coverageAxis} onAxis={setCoverageAxis} grid={sizeGrid} onGrid={setSizeGrid} />
              </div>
            )}
          </>
        ) : tab === 'reporting' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Reporting stock')}</h1>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='Le stock et ses mouvements, sur les axes que vous choisissez. Chaque cellule porte <1>la quantité</1> puis <2>sa valeur {symbole} HT</2> — et la <3>ligne de total</3> reste sous les yeux.'
                    vars={{ symbole: currentCurrency().symbol }}
                    parts={[1, 2, 3].map(() => (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>)} />
                </p>
              </div>
              <button type="button" onClick={() => { if (report && !reportBusy) exportStockReport(report, display, sort, currentLang()); }}
                disabled={!report || reportBusy} title={t('On exporte ce que l’on voit : filtres, colonnes, tri')} className="oz-stats-export"
                style={exportButton(!!report && !reportBusy)}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false} compare="chosen"
                scope={report ? reportScope(report) : ''} periodNote={asOfNote(report?.asOf ?? null, report?.period, report?.reference)}
                options={report?.filterOptions ?? {}} seasons={report?.seasonPeriods ?? []}
                periodEnd={settings.b ? <ReportDisplayToggle value={display} onChange={setDisplay} /> : undefined}
                filtersEnd={<StatsViewsButton key={gln ?? ''} tab="stock-report" enabled={!!gln} adapter={reportViews} />}>
                <StatsLinesRow axes={axes} onChange={changeAxes} legend={stockReportLegend()} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire son stock.')} />}
              {gln && reportStatus.phase === 'failed' && (
                <ReadFailed message={reportStatus.message} framed={false}
                  text={t('La lecture n’a pas abouti : {message}. Aucun chiffre n’est affiché — ce n’est pas un magasin sans stock, c’est une mesure qui n’a pas pu être faite.', { message: reportStatus.message })} />
              )}
              {gln && !report && reportStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Le stock est rejoué depuis le grand livre du magasin, valorisé au PMP.')} />
              )}
              {report && (
                <StockReportTable view={report} display={display} sort={sort} onSort={setSort} busy={reportBusy}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        ) : tab === 'sortie' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Reporting taux de sortie')}</h1>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='Ce qui est <1>entré</1>, ce qui est <2>sorti</2> — et la part écoulée. Les rétrocessions comptent, ou pas : c’est vous qui le dites.'
                    parts={[1, 2].map(() => (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>)} />
                </p>
              </div>
              <RetroModeToggle value={retro} onChange={setRetro} />
              <button type="button" onClick={() => { if (exitReport && !exitBusy) exportStockExit(exitReport, exitDisplay, retro, exitSort, currentLang()); }}
                disabled={!exitReport || exitBusy} title={t('On exporte ce que l’on voit : filtres, colonnes, tri')} className="oz-stats-export"
                style={exportButton(!!exitReport && !exitBusy)}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false} compare="chosen"
                scope={exitReport ? reportScope(exitReport) : ''} periodNote={asOfNote(exitReport?.asOf ?? null, exitReport?.period, exitReport?.reference)}
                options={exitReport?.filterOptions ?? {}} seasons={exitReport?.seasonPeriods ?? []}
                periodEnd={settings.b ? <ReportDisplayToggle value={exitDisplay} onChange={setExitDisplay} /> : undefined}
                filtersEnd={<StatsViewsButton key={gln ?? ''} tab="stock-exit" enabled={!!gln} adapter={exitViews} />}>
                <StatsLinesRow axes={exitAxes} onChange={changeExitAxes} legend={stockExitLegend(retro)} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire son stock.')} />}
              {gln && exitStatus.phase === 'failed' && (
                <ReadFailed message={exitStatus.message} framed={false}
                  text={t('La lecture n’a pas abouti : {message}. Aucun chiffre n’est affiché — ce n’est pas un magasin sans stock, c’est une mesure qui n’a pas pu être faite.', { message: exitStatus.message })} />
              )}
              {gln && !exitReport && exitStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Le stock est rejoué depuis le grand livre du magasin, valorisé au PMP.')} />
              )}
              {exitReport && (
                <StockExitTable view={exitReport} display={exitDisplay} retro={retro} sort={exitSort} onSort={setExitSort} busy={exitBusy}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        ) : tab === 'periodes' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Stock par périodes')}</h1>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='L’état du stock <1>période après période</1> — la quantité, puis ce qu’elle vaut. Un stock est un état : il ne s’additionne pas dans le temps.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={() => { if (periods && !periodBusy) exportStockPeriods(periods, periodSort, currentLang()); }}
                disabled={!periods || periodBusy} title={t('On exporte ce que l’on voit : filtres, colonnes, tri')} className="oz-stats-export"
                style={exportButton(!!periods && !periodBusy)}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false} compare="none"
                scope={periods ? [t('{n} article', { n: periods.scope.references }), t('{n} période', { n: periods.buckets.length })].join(' · ') : ''}
                periodNote={asOfNote(periods?.asOf ?? null, periods?.period)}
                options={periods?.filterOptions ?? {}} seasons={periods?.seasonPeriods ?? []}
                periodEnd={<StockPeriodsControls grain={grain} onGrain={changeGrain} />}
                filtersEnd={<StatsViewsButton key={gln ?? ''} tab="stock-periods" enabled={!!gln} adapter={periodViews} />}>
                <StatsLinesRow axes={periodAxes} onChange={changePeriodAxes} legend={[]} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire son stock.')} />}
              {gln && periodStatus.phase === 'failed' && (
                <ReadFailed message={periodStatus.message} framed={false}
                  text={t('La lecture n’a pas abouti : {message}. Aucun chiffre n’est affiché — ce n’est pas un magasin sans stock, c’est une mesure qui n’a pas pu être faite.', { message: periodStatus.message })} />
              )}
              {gln && !periods && periodStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Le stock est rejoué depuis le grand livre du magasin, valorisé au PMP.')} />
              )}
              {periods && (
                <StockPeriodsTable view={periods} sort={periodSort} onSort={setPeriodSort} busy={periodBusy}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Stock par tailles')}</h1>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='La grille de tailles en <1>colonnes</1>, vos axes en lignes — on voit d’un coup d’œil <2>quelle taille manque et laquelle dort</2>.'
                    parts={[1, 2].map(() => (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>)} />
                </p>
              </div>
              <button type="button" onClick={() => { if (sizeView && !sizeBusy) exportStockSizes(sizeView, sizeMeasures, sizeSort, currentLang()); }}
                disabled={!sizeView || sizeBusy} title={t('On exporte ce que l’on voit : filtres, colonnes, tri')} className="oz-stats-export"
                style={exportButton(!!sizeView && !sizeBusy)}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false} compare="none"
                scope={sizeView ? [t('{n} article sur cette grille', { n: sizeView.scope.references }), t('{n} taille', { n: sizeView.sizes.length })].join(' · ') : ''}
                periodNote={asOfNote(sizeView?.asOf ?? null, sizeView?.period)}
                options={sizeView?.filterOptions ?? {}} seasons={sizeView?.seasonPeriods ?? []}
                periodEnd={<StockSizesControls view={sizeView} measures={sizeMeasures} onMeasures={setSizeMeasures} onGrid={changeGrid} />}
                filtersEnd={<StatsViewsButton key={gln ?? ''} tab="stock-sizes" enabled={!!gln} adapter={sizeViews} />}>
                <StatsLinesRow axes={sizeAxes} onChange={changeSizeAxes} legend={[]} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire son stock.')} />}
              {gln && sizeStatus.phase === 'failed' && (
                <ReadFailed message={sizeStatus.message} framed={false}
                  text={t('La lecture n’a pas abouti : {message}. Aucun chiffre n’est affiché — ce n’est pas un magasin sans stock, c’est une mesure qui n’a pas pu être faite.', { message: sizeStatus.message })} />
              )}
              {gln && !sizeView && sizeStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Le stock est rejoué depuis le grand livre du magasin, valorisé au PMP.')} />
              )}
              {sizeView && sizeView.grid === null && (
                <EmptyState icon="sizeRuler" title={t('Aucune grille de tailles')} note={t('Les grilles de tailles se créent dans Référentiels.')} />
              )}
              {sizeView && sizeView.grid !== null && (
                <StockSizesTable view={sizeView} measures={sizeMeasures} sort={sizeSort} onSort={setSizeSort} busy={sizeBusy}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Les indicateurs ─────────────────────────────────────────────────────────────────────────────

/** Les six indicateurs, dans l'ordre de la rangée : leur nom, leur pictogramme, leur teinte. */
const KPI_FRAMES = {
  value: { label: key('Stock valeur'), icon: 'parcel', tone: 'teal' },
  pieces: { label: key('Stock pièces'), icon: 'crate', tone: 'blue' },
  coverage: { label: key('Couverture'), icon: 'clock', tone: 'violet' },
  sellThrough: { label: key('Taux de sortie'), icon: 'trendUp', tone: 'teal' },
  rotation: { label: key('Rotation'), icon: 'rotate', tone: 'amber' },
  slow: { label: key('Faible sortie'), icon: 'alert', tone: 'rose' },
} as const satisfies Record<string, { label: Translatable; icon: IconName; tone: Tone }>;
type KpiKey = keyof typeof KPI_FRAMES;
const KPI_ORDER: readonly KpiKey[] = ['value', 'pieces', 'coverage', 'sellThrough', 'rotation', 'slow'];

/** Une couverture en jours (« 19 j ») ; « — » quand elle ne se mesure pas. */
const days = (n: number | null): string => (n === null ? '—' : t('{n} j', { n }));
/** Une rotation × 10 en renouvellements par an (« ×17,5 ») ; « — » quand elle ne se mesure pas. */
const rotation = (x10: number | null): string => (x10 === null ? '—' : `×${nombre(x10 / 10, { fractionDigits: 1 })}`);

/** La date d'un jour `AAAAMMJJ`, en chiffres (« 07/09/2026 »). */
const shortDate = (d: string, lang: string): string =>
  new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(Number(d.slice(0, 4)), Number(d.slice(4, 6)) - 1, Number(d.slice(6, 8))));

/** Le Résumé d'une période déjà lue : les six indicateurs et les six cartes. */
export function StockResume({ view, lang, axis, onAxis, grid, onGrid }: {
  view: StockSummaryView; lang: string; axis: SalesSummaryAxis; onAxis: (a: SalesSummaryAxis) => void;
  grid: string | null; onGrid: (id: string) => void;
}) {
  const a = view.totals;
  const r = view.refTotals;
  const tip = (v: string) => (view.reference.kind === 'n1' ? t('N-1 : {valeur}', { valeur: v }) : t('Comparaison : {valeur}', { valeur: v }));
  // ⚠️ PAR NOM ET NON PAR RANG : `Record<KpiKey, …>` réclame chaque indicateur du cadre.
  const kpis: Record<KpiKey, { value: string; growth: number | null; inverse?: boolean; neutral?: boolean; sub: string; ref: string; s: (number | null)[] }> = {
    // ⚠️ UNE VALEUR NON MESURABLE DIT POURQUOI sous son « — » : sans coût d'achat, un stock n'a pas de valeur défendable.
    value: {
      value: eur0(a.valueCents), growth: view.growth.value,
      sub: a.valueCents === null ? t('{n} déclinaison en stock sans coût connu', { n: a.unvaluedRefs }) : t('au {date} · PMP HT', { date: shortDate(view.period.to, lang) }),
      ref: tip(eur0(r.valueCents)), s: view.buckets.map((b) => b.a.valueCents),
    },
    pieces: {
      value: nombre(a.pieces), growth: view.growth.pieces, sub: t('{n} référence en stock', { n: a.references }),
      ref: tip(nombre(r.pieces)), s: view.buckets.map((b) => b.a.pieces),
    },
    // ⚠️ UN ÉCART DE COUVERTURE NE SE JUGE PAS : plus courte, elle peut dire une rupture qui menace ou un stock assaini.
    coverage: { value: days(a.coverageDays), growth: view.growth.coverage, neutral: true, sub: t('au rythme de vente de la période'), ref: tip(days(r.coverageDays)), s: [] },
    sellThrough: {
      value: pc(a.sellThroughBp), growth: view.growth.sellThrough, sub: t('{n} pièce vendue', { n: a.soldQty }),
      ref: tip(pc(r.sellThroughBp)), s: view.buckets.map((b) => b.a.cumSellThroughBp),
    },
    rotation: { value: rotation(a.rotationX10), growth: view.growth.rotation, sub: t('renouvellements du stock par an'), ref: tip(rotation(r.rotationX10)), s: [] },
    // ⚠️ MOINS DE STOCK QUI DORT EST UN PROGRÈS : l'écart se lit à l'envers.
    slow: {
      value: eur0(a.slow.valueCents), growth: view.growth.slow, inverse: true,
      sub: t('{n} réf. · {part} du stock', { n: a.slow.references, part: pc(a.slow.shareBp) }), ref: tip(eur0(r.slow.valueCents)), s: [],
    },
  };
  return (
    <>
      <div style={KPI_ROW}>
        {KPI_ORDER.map((k) => {
          const f = KPI_FRAMES[k];
          const v = kpis[k];
          return (
            <KpiCard key={k} label={t(f.label)} icon={f.icon} tone={f.tone} value={v.value} growth={v.growth} inverse={v.inverse} neutral={v.neutral}
              refLabel={v.ref} sub={v.sub} series={v.s} />
          );
        })}
      </div>
      <div style={CARD_ROW}>
        <Movements view={view} lang={lang} />
        <StockAgeCard age={view.age} />
      </div>
      <div style={CARD_ROW}>
        <Coverage view={view} axis={axis} onAxis={onAxis} />
        <SellThroughCard view={view} lang={lang} />
      </div>
      <div style={CARD_ROW}>
        <SizeCurveCard view={view} grid={grid} onGrid={onGrid} />
        <DormantCard view={view} />
      </div>
    </>
  );
}

// ─── Le stock et ses mouvements ──────────────────────────────────────────────────────────────────

/**
 * LE CADRE DU RÉSUMÉ — le pictogramme, la teinte, le titre et la place de chaque carte, lus par la carte ET par son
 * gabarit de chargement : écrits deux fois, le gabarit cesserait de tomber à la place de ce qu'il annonce.
 */
const FRAMES = {
  movements: { icon: 'parcel', tone: 'teal', title: key('Le stock et ses mouvements'), flex: '2 1 560px', minWidth: 'min(100%,560px)' },
  age: { icon: 'clock', tone: 'violet', title: key('L’âge du stock'), flex: '1 1 300px', minWidth: 'min(100%,290px)' },
  coverage: { icon: 'clock', tone: 'amber', title: key('Combien de jours de vente en stock'), flex: '1.2 1 480px', minWidth: 'min(100%,440px)' },
  sellThrough: { icon: 'trendUp', tone: 'teal', title: key('Ce qui s’écoule, saison par saison'), flex: '1 1 400px', minWidth: 'min(100%,380px)' },
  sizes: { icon: 'sizeRuler', tone: 'blue', title: key('La courbe des tailles : stock contre ventes'), flex: '1.2 1 480px', minWidth: 'min(100%,440px)' },
  dormant: { icon: 'alert', tone: 'rose', title: key('Ce qui dort en réserve'), flex: '1 1 400px', minWidth: 'min(100%,380px)' },
} as const satisfies Record<string, { icon: IconName; tone: Tone; title: Translatable; flex: string; minWidth: string }>;
type FrameKey = keyof typeof FRAMES;
const frameBox = (k: FrameKey): CSSProperties => ({ ...CARD, flex: FRAMES[k].flex, minWidth: FRAMES[k].minWidth });

type Metric = 'val' | 'qty';

/** Ce que la courbe lit d'un seau selon la mesure : la valeur au PMP ou les pièces. */
const LEVEL: Record<Metric, (b: StockSummaryBucket) => number | null> = { val: (b) => b.valueCents, qty: (b) => b.pieces };
const ENTRIES: Record<Metric, (b: StockSummaryBucket) => number | null> = { val: (b) => b.entriesValueCents, qty: (b) => b.entriesQty };
const EXITS: Record<Metric, (b: StockSummaryBucket) => number | null> = { val: (b) => b.exitsValueCents, qty: (b) => b.exitsQty };

/** La phrase de la carte : le sens du stock sur la période, puis ses entrées et ses sorties. */
function movementsInsight(view: StockSummaryView, metric: Metric, unit: 'eur' | 'qty'): string {
  const change = metric === 'val' ? view.levelChange.valueBp : view.levelChange.piecesBp;
  const f = view.totals.flows;
  const entrees = axisLabel((metric === 'val' ? f.entriesValueCents : f.entriesQty) ?? 0, unit);
  const sorties = axisLabel((metric === 'val' ? f.exitsValueCents : f.exitsQty) ?? 0, unit);
  // ⚠️ SANS NIVEAU DE DÉPART, AUCUN SENS : un stock parti de zéro n'est ni « en hausse de x % » ni « stable ».
  if (change === null) return t('Entrées {entrees} · sorties {sorties} sur la période', { entrees, sorties });
  if (change === 0) return t('Stock stable sur la période · entrées {entrees} · sorties {sorties}', { entrees, sorties });
  const taux = pc(Math.abs(change));
  return change > 0
    ? t('Stock en hausse de {taux} sur la période · entrées {entrees} · sorties {sorties}', { taux, entrees, sorties })
    : t('Stock en baisse de {taux} sur la période · entrées {entrees} · sorties {sorties}', { taux, entrees, sorties });
}

/**
 * LE STOCK ET SES MOUVEMENTS — le niveau en fin de chaque seau (aire et ligne) et la référence en pointillé, les entrées et
 * les sorties en barres sous le même axe des temps.
 * ⚠️ TOUTES LES ENTRÉES ET TOUTES LES SORTIES (transferts, casse, retours fournisseur, corrections) : sans elles, le niveau
 *    ne se retrouverait pas en ajoutant ses barres. Le taux de sortie, lui, ne compte que les ventes.
 * ⚠️ UNE VALEUR NON MESURABLE N'EST PAS TRACÉE À ZÉRO : la carte s'ouvre alors sur les pièces, et la valeur dit pourquoi.
 */
function Movements({ view, lang }: { view: StockSummaryView; lang: string }) {
  const valueUnmeasured = view.totals.opening.valueCents === null || view.buckets.some((b) => b.a.valueCents === null);
  const [chosen, setChosen] = useState<Metric | null>(null);
  const { tipProps, tip } = useChartTip();
  const metric: Metric = chosen ?? (valueUnmeasured ? 'qty' : 'val');
  const unit = metric === 'val' ? 'eur' : 'qty';
  const unmeasured = metric === 'val' && valueUnmeasured;
  const refUnmeasured = metric === 'val' && view.buckets.some((b) => b.r !== null && b.r.valueCents === null);
  const n = view.buckets.length;
  const vA = view.buckets.map((b) => LEVEL[metric](b.a) ?? 0);
  const vR = refUnmeasured ? [] : view.buckets.filter((b) => b.r !== null).map((b) => LEVEL[metric](b.r!) ?? 0);
  const start = (metric === 'val' ? view.totals.opening.valueCents : view.totals.opening.pieces) ?? 0;
  const top = niceTop(Math.max(0, start, ...vA, ...vR));
  const X = (i: number) => ((i + 0.5) / n) * 1000;
  const Y = (v: number) => 1000 - (Math.max(0, v) / top) * 1000;
  const lineA = n ? monotonePath(vA.map((v, i) => [X(i), Y(v)])) : '';
  const areaA = lineA ? `${lineA}L${n === 1 ? '1000' : X(n - 1).toFixed(1)},1000L${n === 1 ? '0' : X(0).toFixed(1)},1000Z` : '';
  const lineR = vR.length ? monotonePath(vR.map((v, i) => [X(i), Y(v)])) : '';
  const fMax = Math.max(1, ...view.buckets.map((b) => Math.max(ENTRIES[metric](b.a) ?? 0, EXITS[metric](b.a) ?? 0)));
  const step = Math.max(1, Math.ceil(n / 7));
  const labels = view.buckets.map((b) => bucketLabels(b.key, view.grain, lang));
  const full = (v: number | null | undefined) => (v === null || v === undefined ? '—' : unit === 'qty' ? t('{n} pièce', { n: v }) : eur0(v));
  const refName = view.reference.kind === 'n1' ? t('N-1') : t('Comparaison');
  const insight = n === 0 ? t('Aucun jour ouvré dans la période choisie.') : unmeasured
    ? t('{n} déclinaison en stock sans coût connu : la valeur ne se mesure pas.', { n: view.totals.unvaluedRefs })
    : movementsInsight(view, metric, unit);
  return (
    <div style={frameBox('movements')}>
      <CardHead icon={FRAMES.movements.icon} tone={FRAMES.movements.tone} title={t(FRAMES.movements.title)} insight={insight}>
        <Seg label={t('Mesure affichée')} value={metric} onChange={setChosen}
          options={[{ v: 'val', l: t('Valeur {symbole} HT', { symbole: currentCurrency().symbol }) }, { v: 'qty', l: t('Pièces') }]} />
      </CardHead>
      {n > 0 && unmeasured ? (
        <div style={EMPTY}>{t('La valeur n’est pas mesurable sur cette période : au moins une déclinaison en stock n’a pas de coût d’achat connu.')}</div>
      ) : n > 0 ? (
        <>
          <div style={{ padding: '8px 16px 0' }}>
            <div style={{ position: 'relative', height: 190, marginLeft: 48 }}>
              {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                <div key={f}>
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${f * 100}%`, borderTop: '1px dashed var(--ink-200)' }} />
                  <span style={{
                    position: 'absolute', right: 'calc(100% + 8px)', transform: 'translateY(50%)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)',
                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', bottom: `${f * 100}%`,
                  }}>{axisLabel(top * f, unit)}</span>
                </div>
              ))}
              <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                <defs>
                  <linearGradient id="statsStockGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" style={{ stopColor: 'var(--teal-500)', stopOpacity: 0.3 }} />
                    <stop offset="1" style={{ stopColor: 'var(--teal-500)', stopOpacity: 0 }} />
                  </linearGradient>
                </defs>
                <path d={areaA} style={{ fill: 'url(#statsStockGrad)', stroke: 'none' }} />
                <path d={lineR} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--blue)', strokeWidth: 2, strokeDasharray: '6 5', strokeLinecap: 'round' }} />
                <path d={lineA} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--teal-600)', strokeWidth: 2.6, strokeLinejoin: 'round', strokeLinecap: 'round' }} />
              </svg>
            </div>
            <div style={{ position: 'relative', height: 76, margin: '6px 0 0 48px' }}>
              <span style={{ ...FLOW_LABEL, top: 10, color: 'var(--teal-800)' }}>{t('Entrées')}</span>
              <span style={{ ...FLOW_LABEL, bottom: 10, color: 'var(--amber-fg)' }}>{t('Sorties')}</span>
              <div role="list" style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                {view.buckets.map((b, i) => {
                  const e = ENTRIES[metric](b.a) ?? 0;
                  const o = EXITS[metric](b.a) ?? 0;
                  const flows = t('entrées {entrees} · sorties {sorties}', { entrees: full(e), sorties: full(o) });
                  return (
                    <div key={b.key} role="listitem" className="oz-stats-mv-col"
                      {...tipProps({
                        label: labels[i]!.tip,
                        value: t('Stock {stock}', { stock: full(vA[i]) }),
                        detail: b.r === null || refUnmeasured ? flows : `${refName} ${full(LEVEL[metric](b.r))} · ${flows}`,
                      })}
                      style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 4 }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                        <span style={{ ...BAR, height: `${((e / fMax) * 100).toFixed(1)}%`, background: 'var(--teal-500)', borderRadius: '3px 3px 0 0', transition: 'height .5s cubic-bezier(.2,.7,.3,1)' }} />
                      </div>
                      <div style={{ height: 1, background: 'var(--ink-300)' }} />
                      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                        <span style={{ ...BAR, height: `${((o / fMax) * 100).toFixed(1)}%`, background: 'var(--amber)', borderRadius: '0 0 3px 3px', transition: 'height .5s cubic-bezier(.2,.7,.3,1)' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              {tip}
            </div>
            <div style={{ position: 'relative', height: 24, marginLeft: 48 }}>
              {labels.map((l, i) => (i % step === 0 ? (
                <span key={view.buckets[i]!.key} style={{
                  position: 'absolute', top: 7, left: `${((i + 0.5) / n) * 100}%`, transform: 'translateX(-50%)', fontSize: 'var(--t-sub)',
                  color: 'var(--ink-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                }}>{l.axis}</span>
              ) : null))}
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap', padding: '9px 16px 13px', marginTop: 4,
            borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', color: 'var(--ink-600)',
          }}>
            <span style={LEGEND}>
              <span style={{ width: 18, height: 3, borderRadius: 2, background: 'var(--teal-600)' }} />
              <strong style={{ color: 'var(--ink-800)' }}>{t('Stock')}</strong>{rangeLabel(isoOfYmd(view.period.from), isoOfYmd(view.period.to))}
            </span>
            <span style={LEGEND}>
              <span style={{ width: 18, borderTop: '2px dashed var(--blue)' }} />
              <strong style={{ color: 'var(--ink-800)' }}>{view.reference.kind === 'n1' ? t('N-1, même jour de semaine') : refName}</strong>{rangeLabel(isoOfYmd(view.reference.from), isoOfYmd(view.reference.to))}
              {refUnmeasured && <span>— {t('non mesurable : un coût d’achat manque')}</span>}
            </span>
            <span style={LEGEND}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--teal-500)' }} />{t('Entrées')}</span>
            <span style={LEGEND}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--amber)' }} />{t('Sorties')}</span>
          </div>
        </>
      ) : (
        <div style={EMPTY}>{t('Aucun jour ouvré dans la période choisie — élargissez les dates.')}</div>
      )}
    </div>
  );
}

const FLOW_LABEL: CSSProperties = {
  position: 'absolute', right: 'calc(100% + 8px)', fontSize: 'var(--t-sub)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 600,
};
const BAR: CSSProperties = { width: '60%', maxWidth: 16, minHeight: 1 };
const LEGEND: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 7 };

// ─── Combien de jours de vente en stock ──────────────────────────────────────────────────────────

/** La couleur pleine et l'encre de chaque zone : rose la rupture, vert l'équilibre, ambre le surstock (maquette). */
const ZONE_TONE: Record<StockCoverageRow['zone'], Tone> = { shortage: 'rose', balanced: 'teal', overstock: 'amber' };

/** Les jours d'une ligne : « 28 j », « 180+ j » au-delà du bout de la barre, « aucune vente » sans vente. */
function coverageText(r: StockCoverageRow, capDays: number): string {
  if (r.coverageDays === null) return t('aucune vente');
  return r.coverageDays > capDays ? t('{n}+ j', { n: capDays }) : t('{n} j', { n: r.coverageDays });
}

/** La phrase de la carte : le plus gros surstock et la rupture la plus proche parmi les lignes montrées. */
function coverageInsight(axis: { rows: StockCoverageRow[]; overstock: number | null; shortage: number | null }, capDays: number): string {
  const parts: string[] = [];
  const over = axis.overstock === null ? null : axis.rows[axis.overstock]!;
  const short = axis.shortage === null ? null : axis.rows[axis.shortage]!;
  if (over) {
    parts.push(over.coverageDays === null
      ? t('{nom} : aucune vente sur la période', { nom: shownValue(over.label) })
      : t('{nom} : {jours} de stock', { nom: shownValue(over.label), jours: coverageText(over, capDays) }));
  }
  if (short) parts.push(t('{nom} : {jours}, risque de rupture', { nom: shownValue(short.label), jours: coverageText(short, capDays) }));
  return parts.length ? parts.join(' · ') : t('Couverture équilibrée sur toutes les lignes affichées.');
}

/**
 * COMBIEN DE JOURS DE VENTE EN STOCK — les plus gros stocks d'un axe, chacun avec ses jours de vente au rythme de la période,
 * sur une barre qui va jusqu'au bout de l'échelle et trois zones peintes.
 * ⚠️ LES SEUILS VIENNENT DE LA RÉPONSE (`coverage.thresholds`) : graduations, zones et bout de barre se calculent sur les
 *    nombres qui ont jugé chaque ligne, jamais sur un 30 recopié ici.
 * ⚠️ UNE LIGNE SANS VENTE va au bout de la barre et se dit « aucune vente » : son stock dort.
 */
function Coverage({ view, axis, onAxis }: { view: StockSummaryView; axis: SalesSummaryAxis; onAxis: (a: SalesSummaryAxis) => void }) {
  const th = view.coverage.thresholds;
  const { tipProps, tip } = useChartTip();
  const data = view.coverage.axes[axis];
  const cap = th.coverageCapDays;
  const at = (d: number) => `${((d / cap) * 100).toFixed(2)}%`;
  return (
    <div style={frameBox('coverage')}>
      <CardHead icon={FRAMES.coverage.icon} tone={FRAMES.coverage.tone} title={t(FRAMES.coverage.title)} insight={data.rows.length ? coverageInsight(data, cap) : t('Aucun stock dans le périmètre.')}>
        <Seg label={t('Regrouper par')} value={axis} onChange={onAxis} options={AXES.map((a) => ({ v: a.v, l: fieldLabel(a.field) }))} />
      </CardHead>
      {data.rows.length ? (
        <>
          <div style={{ padding: '8px 16px 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ width: 120, flexShrink: 0 }} />
              <span style={{ flex: 1, position: 'relative', height: 14, fontSize: 'var(--t-micro)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ position: 'absolute', left: 0 }}>0</span>
                <span style={{ position: 'absolute', left: at(th.shortageBelowDays), transform: 'translateX(-50%)' }}>{t('{n} j', { n: th.shortageBelowDays })}</span>
                <span style={{ position: 'absolute', left: at(th.overstockAboveDays), transform: 'translateX(-50%)' }}>{t('{n} j', { n: th.overstockAboveDays })}</span>
                <span style={{ position: 'absolute', right: 0 }}>{t('{n} j+', { n: cap })}</span>
              </span>
              <span style={{ width: 132, flexShrink: 0 }} />
            </div>
            <div role="list" aria-label={t(FRAMES.coverage.title)}>
            {data.rows.map((r) => {
              const tone = TONES[ZONE_TONE[r.zone]];
              const days = coverageText(r, cap);
              const width = r.coverageDays === null ? 100 : Math.max(2, Math.min(100, (r.coverageDays / cap) * 100));
              // ⚠️ LA LIGNE DIT DÉJÀ SON NOM, SES JOURS ET SA VALEUR : la bulle les reprend et ajoute ses pièces, sans la renommer.
              const tipContent = {
                label: shownValue(r.label),
                value: r.coverageDays === null ? t('aucune vente sur la période') : t('{jours} de vente en stock', { jours: t('{n} jour', { n: r.coverageDays }) }),
                detail: `${t('{n} pièce', { n: r.pieces })} · ${eur0(r.valueCents)}`,
              };
              return (
                <div key={r.label} role="listitem" {...tipProps(tipContent, { names: false })}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--ink-100)' }}>
                  <span style={{
                    width: 120, flexShrink: 0, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{shownValue(r.label)}</span>
                  <span style={{ flex: 1, position: 'relative', height: 14, borderRadius: 'var(--r-pill)', overflow: 'hidden', background: 'var(--ink-100)' }}>
                    <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: at(th.shortageBelowDays), background: 'var(--rose-bg)' }} />
                    <span style={{ position: 'absolute', left: at(th.overstockAboveDays), top: 0, bottom: 0, right: 0, background: 'var(--amber-bg)' }} />
                    <span style={{
                      position: 'absolute', left: 0, top: 3, bottom: 3, width: `${width.toFixed(1)}%`, borderRadius: 'var(--r-pill)', background: tone[2],
                      transition: 'width .5s cubic-bezier(.2,.7,.3,1)',
                    }} />
                  </span>
                  <span style={{
                    width: 62, flexShrink: 0, textAlign: 'right', fontSize: 'var(--t-body)', fontWeight: 700, color: tone[1], fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                  }}>{days}</span>
                  <span style={{
                    width: 60, flexShrink: 0, textAlign: 'right', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                  }}>{eur0(r.valueCents)}</span>
                </div>
              );
            })}
            </div>
          </div>
          {tip}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap', padding: '9px 16px 13px', fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
            <span style={LEGEND}><span style={{ width: 10, height: 10, borderRadius: 3, background: TONES.rose[2] }} />{t('Moins de {n} j : risque de rupture', { n: th.shortageBelowDays })}</span>
            <span style={LEGEND}><span style={{ width: 10, height: 10, borderRadius: 3, background: TONES.teal[2] }} />{t('{de} à {a} j : équilibré', { de: th.shortageBelowDays, a: th.overstockAboveDays })}</span>
            <span style={LEGEND}><span style={{ width: 10, height: 10, borderRadius: 3, background: TONES.amber[2] }} />{t('Plus de {n} j : surstock', { n: th.overstockAboveDays })}</span>
          </div>
        </>
      ) : (
        <div style={EMPTY}>{t('Aucun stock dans le périmètre.')}</div>
      )}
    </div>
  );
}

// ─── Ce qui s'écoule, saison par saison ──────────────────────────────────────────────────────────

/** La couleur et l'encre d'un taux : vert dès 60 %, ambre sous 25 %, bleu entre les deux (maquette). */
const SELL_THROUGH_TONE: Record<NonNullable<StockSellThroughRow['zone']>, Tone> = { fast: 'teal', steady: 'blue', slow: 'amber' };

/**
 * Le débord d'une ligne de saison au-delà de la carte : son surlignage touche les bords, son texte reste aligné sur le
 * reste. ⚠️ UNE CONSTANTE POUR LES DEUX : le rembourrage et la marge qui l'annule ne peuvent pas se désaccorder.
 */
const SEASON_ROW_BLEED = 8;

/** Un taux arrondi au point, pour la phrase de la carte (« 47 % »). */
const pctRounded = (bp: number | null): string => (bp === null ? '—' : `${nombre(Math.round(bp / 100))} %`);

/** Le nom court d'une ligne de saison : celui de ses dates (`seasonName`, la règle de la barre), « Permanent », « Non datée ». */
function rowSeasonName(row: StockSellThroughRow): string {
  if (row.key === 'permanent') return t('Permanent');
  if (row.key === 'undated') return t('Non datée');
  return seasonName(row.from ?? '', row.to ?? '', row.codes);
}

/** La phrase de la carte : les trois premières saisons et ce qui s'en est écoulé. */
function sellThroughInsight(rows: StockSellThroughRow[]): string {
  if (!rows.length) return t('Aucune saison dans le périmètre.');
  return rows.slice(0, 3).map((r) => t('{saison} : {taux} écoulé', { saison: rowSeasonName(r), taux: pctRounded(r.sellThroughBp) })).join(' · ');
}

/** La couverture d'un écoulement, en semaines ; « aucune vente » quand rien ne l'écoule, « — » sur un stock négatif. */
const paceCoverage = (p: StockPace): string =>
  (p.coverageWeeks !== null ? t('{n} sem', { n: p.coverageWeeks }) : p.pieces < 0 ? '—' : t('aucune vente'));

/**
 * CE QUI S'ÉCOULE, SAISON PAR SAISON — le taux de sortie de chaque saison sur la période, sous le repère de la moyenne du
 * magasin ; une saison cliquée restreint l'écoulement des 12 dernières semaines (ventes par semaine, vitesse, couverture,
 * stock).
 * ⚠️ UNE LIGNE PAR SAISON DATÉE (décision utilisateur) : les codes qui partagent les mêmes dates sont réunis par le serveur ;
 *    l'infobulle les nomme.
 * ⚠️ LA MOYENNE EST LA TUILE « TAUX DE SORTIE » : la même règle, le même nombre.
 * ⚠️ « VENDUES », PAS « SORTIES » : le taux compte les ventes ; la casse et les transferts sont dans les mouvements.
 */
function SellThroughCard({ view, lang }: { view: StockSummaryView; lang: string }) {
  const [picked, setPicked] = useState<string | null>(null);
  const { tipProps, tip } = useChartTip();
  const rows = view.sellThrough.rows;
  const selected = rows.find((r) => r.key === picked) ?? null;
  const pace: StockPace = selected ?? view.sellThrough.all;
  const avg = view.totals.sellThroughBp;
  const top = Math.max(1, ...pace.weeks);
  const sold = pace.weeks.reduce((s, x) => s + x, 0);
  const stat = (label: string, value: string) => (
    <div key={label} style={{ padding: '8px 10px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', minWidth: 0 }}>
      <span style={{
        display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase',
        color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{label}</span>
      <span style={{ display: 'block', marginTop: 3, fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
  return (
    <div style={frameBox('sellThrough')}>
      <CardHead icon={FRAMES.sellThrough.icon} tone={FRAMES.sellThrough.tone} title={t(FRAMES.sellThrough.title)} insight={sellThroughInsight(rows)} />
      <div style={{ padding: '8px 16px 14px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map((r) => {
            const on = r.key === selected?.key;
            const tone = r.zone ? TONES[SELL_THROUGH_TONE[r.zone]] : null;
            const width = r.sellThroughBp === null ? 0 : Math.max(1.5, r.sellThroughBp / 100);
            return (
              <button key={r.key} type="button" aria-pressed={on} onClick={() => setPicked(on ? null : r.key)} className="oz-stats-season"
                {...tipProps({ label: rowSeasonName(r), value: pc(r.sellThroughBp), detail: t('{codes} — voir l’écoulement de cette saison', { codes: r.codes.map(shownValue).join(' · ') }) }, { names: false })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: `9px ${SEASON_ROW_BLEED}px`, marginLeft: -SEASON_ROW_BLEED,
                  marginRight: -SEASON_ROW_BLEED, borderRadius: 'var(--r-sm)', border: 'none',
                  borderBottom: '1px solid var(--ink-100)', background: on ? 'var(--teal-50)' : 'transparent',
                  boxShadow: `inset 0 0 0 1px ${on ? 'var(--teal-400)' : 'transparent'}`, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit',
                }}>
                <span style={{ width: 118, flexShrink: 0, minWidth: 0 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)',
                    fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700, color: 'var(--ink-800)',
                  }}>{rowSeasonName(r)}</span>
                  <span style={{ display: 'block', marginTop: 3, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.key === 'permanent' || r.key === 'undated' ? r.codes.map(shownValue).join(' · ') : t(AGE_FRAMES[r.age].label)}
                  </span>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', position: 'relative', height: 12, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)' }}>
                    <span style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: `${width.toFixed(1)}%`, borderRadius: 'var(--r-pill)',
                      background: tone ? tone[2] : 'transparent', transition: 'width .5s cubic-bezier(.2,.7,.3,1)',
                    }} />
                    {avg !== null && (
                      <span style={{ position: 'absolute', top: -4, bottom: -4, left: `${(avg / 100).toFixed(1)}%`, borderLeft: '2px dashed var(--ink-700)' }} />
                    )}
                  </span>
                  <span style={{ display: 'block', marginTop: 5, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
                    {t('{n} vendue', { n: r.soldQty })} · {t('{n} en stock', { n: r.pieces })}
                  </span>
                </span>
                <span style={{
                  width: 54, flexShrink: 0, textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 800, color: tone ? tone[1] : 'var(--ink-500)',
                  fontVariantNumeric: 'tabular-nums',
                }}>{pc(r.sellThroughBp)}</span>
              </button>
            );
          })}
        </div>
        {rows.length === 0 && <div style={EMPTY}>{t('Aucune saison dans le périmètre.')}</div>}
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 10, fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
          <span aria-hidden style={{ width: 0, height: 12, borderLeft: '2px dashed var(--ink-700)' }} />
          {t('moyenne {taux} · taux de sortie = vendu ÷ (vendu + stock) · cliquez une saison pour son écoulement', { taux: pc(avg) })}
        </span>
        <div style={{ marginTop: 14, paddingTop: 13, borderTop: '1px solid var(--ink-200)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>
              {t('Écoulement des 12 dernières semaines')}{' '}
              <span style={{ fontWeight: 600, color: 'var(--ink-500)' }}>· {selected ? t('saison {saison}', { saison: rowSeasonName(selected) }) : t('toutes saisons')}</span>
            </span>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
              <Trans k='<1>{n}</1> pièce vendue' vars={{ n: sold }} parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} />
            </span>
          </div>
          <div role="list" style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 86, marginTop: 12 }}>
            {pace.weeks.map((v, i) => {
              const last = i === pace.weeks.length - 1;
              const week = bucketLabels(view.sellThrough.weekStarts[i] ?? '', 'week', lang).tip;
              return (
                <div key={i} role="listitem" {...tipProps({ label: week, value: t('{n} pièce vendue', { n: v }), detail: selected ? rowSeasonName(selected) : t('toutes saisons') })}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', gap: 4 }}>
                  <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'flex-end' }}>
                    <div style={{
                      width: '100%', flexShrink: 0, height: `${Math.round((Math.max(0, v) / top) * 100)}%`, minHeight: 2,
                      background: last ? 'var(--teal-500)' : 'var(--teal-300)', borderRadius: '5px 5px 2px 2px', transition: 'height .5s cubic-bezier(.2,.7,.3,1)',
                    }} />
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--ink-400)', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {last ? t('S|semaine en cours') : t('S-{n}', { n: pace.weeks.length - 1 - i })}
                  </div>
                </div>
              );
            })}
          </div>
          {tip}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8, marginTop: 11 }}>
            {stat(t('Vitesse de vente'), t('{v} par semaine', { v: nombre(pace.velocityX10 / 10, { fractionDigits: 1 }) }))}
            {stat(t('Couverture'), paceCoverage(pace))}
            {stat(t('En stock'), t('{n} pcs', { n: pace.pieces }))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Le gabarit de « Ce qui s'écoule » pendant la première mesure : ses lignes et ses semaines en matière animée. */
function SellThroughPlaceholder() {
  return (
    <div style={frameBox('sellThrough')}>
      <CardHead icon={FRAMES.sellThrough.icon} tone={FRAMES.sellThrough.tone} title={t(FRAMES.sellThrough.title)} insight={t('Mesure en cours…')} />
      <div style={{ padding: '8px 16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[0, 1, 2].map((i) => <Shimmer key={i} w="100%" h={38} r="var(--r-sm)" />)}
        <Shimmer w="100%" h={86} r="var(--r-sm)" />
      </div>
    </div>
  );
}

// ─── L'âge du stock ──────────────────────────────────────────────────────────────────────────────

/** Le nom et la couleur de chaque âge : violet l'à-venir, vert l'en-cours, bleu le précédent, rose le plus ancien (maquette). */
const AGE_FRAMES: Record<StockAge, { label: Translatable; color: string }> = {
  next: { label: key('À venir|saison'), color: TONES.violet[2] },
  current: { label: key('Saison en cours'), color: TONES.teal[2] },
  previous: { label: key('Saison précédente'), color: TONES.blue[2] },
  old: { label: key('Plus d’un an'), color: TONES.rose[2] },
  permanent: { label: key('Permanent'), color: 'var(--ink-300)' },
  // ⚠️ NON DATÉE n'est pas dans la maquette, qui range tout code inconnu en « Permanent » : une saison dont le code ne dit
  //    pas les dates n'est pas permanente pour autant, et l'écran le dit plutôt que de la ranger.
  undated: { label: key('Non datée'), color: 'var(--ink-200)' },
};

/** La phrase de la carte : la part du stock qui a plus d'un an, puis celle de la saison en cours. */
function ageInsight(age: StockSummaryView['age']): string {
  if (!age.buckets.length) return t('Aucun stock à la date.');
  const of = (k: StockAge) => age.buckets.find((b) => b.age === k);
  const old = of('old');
  const current = of('current');
  const parts = [old ? t('{part} du stock vient de saisons de plus d’un an', { part: pc(old.shareBp) }) : t('Aucun stock de plus d’un an')];
  if (current) parts.push(t('{part} de la saison en cours', { part: pc(current.shareBp) }));
  return parts.join(' · ');
}

/**
 * L'ÂGE DU STOCK — le stock de fin de période réparti par âge de sa saison d'origine : une barre partagée, puis chaque âge
 * avec ses saisons, sa valeur et sa part.
 * ⚠️ LES DATES D'UNE SAISON SONT CELLES DU RÉFÉRENTIEL DES SAISONS — posées par le commerçant, sinon tirées de son code
 *    (`core-catalog`) — et la note dit où les voir : une saison dont rien ne dit les dates est « non datée », jamais rangée
 *    au hasard.
 * ⚠️ QUAND LA VALEUR DU STOCK NE SE MESURE PAS, la carte parle en PIÈCES (`age.basis`) et sa note dit pourquoi.
 */
function StockAgeCard({ age }: { age: StockSummaryView['age'] }) {
  const byValue = age.basis === 'value';
  const { tipProps, tip } = useChartTip();
  const amount = (b: StockSummaryView['age']['buckets'][number]): string => (byValue ? eur0(b.valueCents) : t('{n} pièce', { n: b.pieces }));
  return (
    <div style={frameBox('age')}>
      <CardHead icon={FRAMES.age.icon} tone={FRAMES.age.tone} title={t(FRAMES.age.title)} insight={ageInsight(age)} />
      {age.buckets.length ? (
        <div style={{ padding: '10px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
          <div role="list" style={{ display: 'flex', gap: 2, height: 34, borderRadius: 9, overflow: 'hidden', background: 'var(--ink-100)' }}>
            {age.buckets.map((b) => (
              <div key={b.age} role="listitem"
                {...tipProps({ label: t(AGE_FRAMES[b.age].label), value: amount(b), detail: t('{part} du stock', { part: pc(b.shareBp) }) })}
                style={{ width: `${(b.shareBp / 100).toFixed(2)}%`, minWidth: 3, background: AGE_FRAMES[b.age].color, transition: 'width .5s cubic-bezier(.2,.7,.3,1)' }} />
            ))}
          </div>
          {tip}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {age.buckets.map((b) => (
              <div key={b.age} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--ink-100)' }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: AGE_FRAMES[b.age].color, flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{t(AGE_FRAMES[b.age].label)}</span>
                  <span style={{
                    display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{b.codes.map(shownValue).join(' · ')}</span>
                </span>
                <span style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{amount(b)}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{pc(b.shareBp)}</span>
                </span>
              </div>
            ))}
          </div>
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.45, marginTop: 'auto' }}>
            {byValue
              ? t('Âge lu sur la saison d’origine, datée en Référentiels, valeur au PMP HT.')
              : t('Âge lu sur la saison d’origine, datée en Référentiels, en pièces : la valeur du stock ne se mesure pas.')}
          </span>
        </div>
      ) : (
        <div style={EMPTY}>{t('Aucun stock dans le périmètre.')}</div>
      )}
    </div>
  );
}

/** Le gabarit de « L'âge du stock » pendant la première mesure : la barre et les lignes en matière animée. */
function StockAgePlaceholder() {
  return (
    <div style={frameBox('age')}>
      <CardHead icon={FRAMES.age.icon} tone={FRAMES.age.tone} title={t(FRAMES.age.title)} insight={t('Mesure en cours…')} />
      <div style={{ padding: '10px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        <Shimmer w="100%" h={34} r="9px" />
        {[0, 1, 2, 3].map((i) => <Shimmer key={i} w="100%" h={30} r="var(--r-sm)" />)}
      </div>
    </div>
  );
}

// ─── La courbe des tailles ───────────────────────────────────────────────────────────────────────

/** La courbe de la grille choisie : celle que l'écran retient si elle est encore là, sinon la plus stockée. */
const shownGrid = (view: StockSummaryView, id: string | null): StockSizeCurve | null =>
  view.sizes.grids.find((g) => g.id === id) ?? view.sizes.grids[0] ?? null;

/** Un écart en points, signé (« +6 », « −5 ») — le moins typographique, celui de la maquette. */
const gapText = (points: number): string => `${points > 0 ? '+' : '−'}${nombre(Math.abs(points))}`;

/** La phrase de la carte : la taille la plus surreprésentée, la plus sous-représentée, ou une courbe qui suit les ventes. */
function sizesInsight(g: StockSizeCurve | null): string {
  if (!g) return t('Aucune grille de tailles dans le périmètre.');
  const over = g.over === null ? null : g.rows[g.over]!;
  const under = g.under === null ? null : g.rows[g.under]!;
  const pts = (r: { gapPoints: number | null }) => t('{n} pt', { n: Math.abs(r.gapPoints ?? 0) });
  if (over && under) return t('Trop de {sur} (+{ecartSur}) · pas assez de {sous} (−{ecartSous})', { sur: over.size, ecartSur: pts(over), sous: under.size, ecartSous: pts(under) });
  if (over) return t('Trop de {taille} (+{ecart})', { taille: over.size, ecart: pts(over) });
  if (under) return t('Pas assez de {taille} (−{ecart})', { taille: under.size, ecart: pts(under) });
  return t('Le stock suit la courbe des ventes, taille par taille.');
}

/** Ce que dit la part des ventes d'une taille dans sa bulle : la part, et si la taille est sur- ou sous-représentée. */
function salesShareDetail(r: StockSizeCurve['rows'][number], gapMin: number): string {
  if (r.salesShareBp === null) return t('aucune vente sur la période');
  if (r.gapPoints !== null && r.gapPoints >= gapMin) return t('{part} des ventes — surreprésentée', { part: pc(r.salesShareBp) });
  if (r.gapPoints !== null && r.gapPoints <= -gapMin) return t('{part} des ventes — sous-représentée, risque de rupture', { part: pc(r.salesShareBp) });
  return t('{part} des ventes', { part: pc(r.salesShareBp) });
}

/** Le repère d'une taille : ambre quand il y a trop de stock, rose quand il n'y en a pas assez. */
const GAP_TONE = (points: number): Tone => (points > 0 ? 'amber' : 'rose');

/** Le repère d'un écart, dans la carte et dans sa légende. */
function GapBadge({ points, height, size }: { points: number; height: number; size: string }) {
  const tone = TONES[GAP_TONE(points)];
  return (
    <span style={{
      height, padding: `0 ${height === 18 ? 6 : 5}px`, borderRadius: 'var(--r-pill)', background: tone[0], color: tone[1], fontSize: size, fontWeight: 800,
      display: 'inline-flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums',
    }}>{gapText(points)}</span>
  );
}

/**
 * LA COURBE DES TAILLES : pour une grille, la part du stock et la part des ventes de chaque taille, et un repère dès que
 * l'écart atteint le seuil servi (3 points) — trop de stock en ambre, pas assez en rose.
 * ⚠️ LES PARTS, LES ÉCARTS ET LE SEUIL VIENNENT DU SERVEUR ; l'écran ne calcule que la hauteur des barres (la plus haute part
 *    de la grille, plus 18 %, fait toute la hauteur — la marge de la maquette, qui laisse la place du repère).
 * ⚠️ DES PIÈCES SANS TAILLE NE SONT PAS SUR LA COURBE, et la carte le dit : les taire laisserait croire que la courbe compte
 *    tout le stock de la grille.
 */
function SizeCurveCard({ view, grid, onGrid }: { view: StockSummaryView; grid: string | null; onGrid: (id: string) => void }) {
  const { tipProps, tip } = useChartTip();
  const g = shownGrid(view, grid);
  const gapMin = view.coverage.thresholds.sizeGapPoints;
  const top = g ? Math.max(1, ...g.rows.flatMap((r) => [r.stockShareBp ?? 0, r.salesShareBp ?? 0])) * 1.18 : 1;
  const height = (bp: number | null) => `${(((bp ?? 0) / top) * 100).toFixed(1)}%`;
  return (
    <div style={frameBox('sizes')}>
      <CardHead icon={FRAMES.sizes.icon} tone={FRAMES.sizes.tone} title={t(FRAMES.sizes.title)} insight={sizesInsight(g)}>
        {g && <Seg label={fieldLabel('sizeGrid')} value={g.id} onChange={onGrid} options={view.sizes.grids.map((x) => ({ v: x.id, l: x.name }))} />}
      </CardHead>
      {g ? (
        <>
          <div role="list" style={{ display: 'flex', gap: 6, height: 210, padding: '16px 16px 0', alignItems: 'stretch' }}>
            {g.rows.map((r) => (
              <div key={r.size} role="listitem" className="oz-stats-size-col"
                {...tipProps({
                  label: `${fieldLabel('size')} ${r.size}`,
                  value: t('{part} du stock ({pieces})', { part: pc(r.stockShareBp), pieces: t('{n} pièce', { n: r.pieces }) }),
                  detail: salesShareDetail(r, gapMin),
                })}
                style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 6 }}>
                <div style={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {r.gapPoints !== null && Math.abs(r.gapPoints) >= gapMin && <GapBadge points={r.gapPoints} height={18} size="var(--t-sub)" />}
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 3 }}>
                  {[[r.stockShareBp, 'var(--teal-500)'], [r.salesShareBp, 'var(--blue)']].map(([bp, color]) => (
                    <span key={String(color)} style={{
                      width: '34%', maxWidth: 22, height: height(bp as number | null), background: String(color), borderRadius: '4px 4px 0 0',
                      transition: 'height .5s cubic-bezier(.2,.7,.3,1)',
                    }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          {tip}
          <div style={{ display: 'flex', gap: 6, padding: '7px 16px 0', borderTop: '1px solid var(--ink-200)' }}>
            {g.rows.map((r) => (
              <span key={r.size} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-800)' }}>{r.size}</span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap', padding: '10px 16px 13px', fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
            <span style={LEGEND}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--teal-500)' }} />{t('Part du stock')}</span>
            <span style={LEGEND}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--blue)' }} />{t('Part des ventes')}</span>
            <span style={LEGEND}><GapBadge points={6} height={16} size="var(--t-micro)" />{t('trop de stock')}</span>
            <span style={LEGEND}><GapBadge points={-5} height={16} size="var(--t-micro)" />{t('pas assez — risque de rupture')}</span>
            {g.unsizedPieces > 0 && <span>{t('{n} pièce sans taille, hors de la courbe', { n: g.unsizedPieces })}</span>}
          </div>
        </>
      ) : (
        <div style={EMPTY}>{t('Aucune grille de tailles dans le périmètre.')}</div>
      )}
    </div>
  );
}

// ─── Ce qui dort en réserve ──────────────────────────────────────────────────────────────────────

/** La phrase de la carte : ce que les références montrées immobilisent, en valeur — en pièces quand elle ne se mesure pas. */
function dormantInsight(d: StockSummaryView['dormant']): string {
  if (!d.rows.length) return t('Rien ne dort dans le périmètre.');
  const amount = d.basis === 'value' ? eur0(d.valueCents) : t('{n} pièce', { n: d.pieces });
  return t('{n} référence immobilise {montant} ({part} du stock)', { n: d.rows.length, montant: amount, part: pc(d.shareBp) });
}

/**
 * CE QUI DORT EN RÉSERVE : les références au score « valeur × (1 − taux de sortie) » le plus haut, leur stock, leur sortie et
 * leur couverture. Le serveur les range ; la sortie se peint en ambre sous le seuil de la faible sortie, en bleu au-dessus.
 */
function DormantCard({ view }: { view: StockSummaryView }) {
  const d = view.dormant;
  return (
    <div style={frameBox('dormant')}>
      <CardHead icon={FRAMES.dormant.icon} tone={FRAMES.dormant.tone} title={t(FRAMES.dormant.title)} insight={dormantInsight(d)} />
      {d.rows.length ? (
        <div style={{ padding: '6px 16px 14px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 6px', ...MONO_LABEL }}>
            <span style={{ width: 20 }} /><span style={{ flex: 1 }}>{t('Article')}</span>
            <span style={{ width: 58, textAlign: 'right' }}>{t('Sortie|taux')}</span><span style={{ width: 52, textAlign: 'right' }}>{t('Couv.')}</span>
          </div>
          {d.rows.map((r, i) => <DormantLine key={r.model} r={r} rank={i + 1} />)}
        </div>
      ) : (
        <div style={EMPTY}>{t('Rien ne dort dans le périmètre.')}</div>
      )}
    </div>
  );
}

/** Une référence qui dort : son rang, son code, son nom, son stock, sa sortie et sa couverture. */
function DormantLine({ r, rank }: { r: StockDormantRow; rank: number }) {
  const tone = TONES[r.slow ? 'amber' : 'blue'];
  const ellipsis: CSSProperties = { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--ink-100)' }}>
      <span style={{
        width: 20, height: 20, borderRadius: 'var(--r-pill)', background: TONES.rose[0], color: TONES.rose[1], fontSize: 'var(--t-sub)', fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{nombre(rank)}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...ellipsis, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)' }}>{r.model}</span>
        <span style={{ ...ellipsis, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{r.label}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>
          {`${t('{n} pcs', { n: r.pieces })} · ${eur0(r.valueCents)}`}
        </span>
      </span>
      <span style={{ width: 58, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{
          height: 20, padding: '0 7px', borderRadius: 'var(--r-pill)', background: tone[0], color: tone[1], fontSize: 'var(--t-meta)', fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums',
        }}>{pc(r.sellThroughBp)}</span>
      </span>
      <span style={{ width: 52, flexShrink: 0, textAlign: 'right', fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums' }}>
        {days(r.coverageDays)}
      </span>
    </div>
  );
}

/** Le gabarit des deux dernières cartes pendant la première mesure. */
function SizesDormantPlaceholder() {
  return (
    <>
      <div style={frameBox('sizes')}>
        <CardHead icon={FRAMES.sizes.icon} tone={FRAMES.sizes.tone} title={t(FRAMES.sizes.title)} insight={t('Mesure en cours…')} />
        <div style={{ padding: '16px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
          <Shimmer w="100%" h={210} r="var(--r-sm)" />
        </div>
      </div>
      <div style={frameBox('dormant')}>
        <CardHead icon={FRAMES.dormant.icon} tone={FRAMES.dormant.tone} title={t(FRAMES.dormant.title)} insight={t('Mesure en cours…')} />
        <div style={{ padding: '6px 16px 14px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => <Shimmer key={i} w="100%" h={38} r="var(--r-sm)" />)}
        </div>
      </div>
    </>
  );
}

// ─── Le gabarit de la première mesure ────────────────────────────────────────────────────────────

/**
 * LE RÉSUMÉ PENDANT SA PREMIÈRE MESURE — le cadre réel (les six indicateurs et les cartes, leurs titres et pictogrammes), une
 * matière animée à la place des chiffres et des courbes ; aucun chiffre, aucune courbe dessinée (§23.0). Même règle que le
 * Résumé des ventes.
 */
function StockResumePlaceholder() {
  return (
    <div aria-busy="true">
      <div style={KPI_ROW}>
        {KPI_ORDER.map((k) => {
          const f = KPI_FRAMES[k];
          return (
            <div key={k} style={KPI_TILE}>
              <KpiLabel label={t(f.label)} icon={f.icon} tone={f.tone} />
              <Shimmer w="62%" h={26} style={{ marginTop: 10 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <Shimmer w={46} h={18} r="var(--r-pill)" />
                <Shimmer w="38%" h={10} />
              </div>
              <Shimmer w={`calc(100% + ${2 * KPI_PAD_X}px)`} h={34} r="0" tone={f.tone} style={{ margin: `9px -${KPI_PAD_X}px 0` }} />
            </div>
          );
        })}
      </div>
      <div style={CARD_ROW}>
        <div style={frameBox('movements')}>
          <CardHead icon={FRAMES.movements.icon} tone={FRAMES.movements.tone} title={t(FRAMES.movements.title)} insight={t('Mesure en cours…')} />
          <div style={{ padding: '10px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Le stock est rejoué du grand livre de stock du magasin, jour après jour.')}</span>
            <div style={{ position: 'relative', height: 190 }}>
              <Shimmer w="100%" h={190} r="var(--r-sm)" style={{ opacity: 0.55 }} />
              {[0, 0.25, 0.5, 0.75, 1].map((p) => (
                <span key={p} aria-hidden style={{ position: 'absolute', left: 0, right: 0, bottom: `${p * 100}%`, borderTop: '1px dashed var(--ink-200)' }} />
              ))}
            </div>
            <Shimmer w="100%" h={76} r="var(--r-sm)" />
          </div>
        </div>
        <StockAgePlaceholder />
      </div>
      <div style={CARD_ROW}>
        <div style={frameBox('coverage')}>
          <CardHead icon={FRAMES.coverage.icon} tone={FRAMES.coverage.tone} title={t(FRAMES.coverage.title)} insight={t('Mesure en cours…')} />
          <div style={{ padding: '10px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
            <Shimmer w="100%" h={200} r="var(--r-sm)" />
          </div>
        </div>
        <SellThroughPlaceholder />
      </div>
      <div style={CARD_ROW}>
        <SizesDormantPlaceholder />
      </div>
    </div>
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les indicateurs de la période et de la comparaison, le stock et ses mouvements seau par
 * seau, l'âge du stock, les jours de vente en stock de l'axe affiché, l'écoulement saison par saison et ses semaines, la courbe
 * de la grille affichée, puis ce qui dort.
 * ⚠️ UN NOMBRE INCONNU RESTE VIDE dans le fichier — un 0 dans un tableur devient une somme fausse.
 */
export function exportStock(view: StockSummaryView, axis: SalesSummaryAxis, lang: string, grid: string | null = null): void {
  const sizes = shownGrid(view, grid);
  const code = currentCurrency().code;
  const money = (c: number | null) => toInputValue(c);
  const int = (v: number | null) => (v === null ? '' : String(v));
  const kpi = (l: Translatable, x: string, y: string): string[] => [t(l, { code }), x, y];
  const a = view.totals;
  const r = view.refTotals;
  exporterCsv(`resume-stock-${view.period.from}-${view.period.to}`, [t('Indicateur'), t('Période'), t('Comparaison')], [
    kpi(key('Stock valeur au PMP HT ({code})'), money(a.valueCents), money(r.valueCents)),
    kpi(key('Stock pièces'), int(a.pieces), int(r.pieces)),
    kpi(key('Références en stock'), int(a.references), int(r.references)),
    kpi(key('Déclinaisons en stock sans coût connu'), int(a.unvaluedRefs), int(r.unvaluedRefs)),
    kpi(key('Pièces vendues'), int(a.soldQty), int(r.soldQty)),
    kpi(key('Couverture (jours)'), int(a.coverageDays), int(r.coverageDays)),
    kpi(key('Taux de sortie (%)'), csvRate(a.sellThroughBp), csvRate(r.sellThroughBp)),
    kpi(key('Rotation (par an)'), a.rotationX10 === null ? '' : toInputValue(a.rotationX10, 1), r.rotationX10 === null ? '' : toInputValue(r.rotationX10, 1)),
    kpi(key('Faible sortie ({code})'), money(a.slow.valueCents), money(r.slow.valueCents)),
    kpi(key('Faible sortie, références'), int(a.slow.references), int(r.slow.references)),
    kpi(key('Faible sortie, part du stock (%)'), csvRate(a.slow.shareBp), csvRate(r.slow.shareBp)),
    // Le stock et ses mouvements suivent, sous une ligne vide : leurs colonnes ne sont pas celles des indicateurs.
    [],
    [t('Période'), t('Stock pièces'), t('Stock valeur ({code})', { code }), t('Entrées (pièces)'), t('Sorties (pièces)'),
      t('Entrées ({code})', { code }), t('Sorties ({code})', { code }), t('Pièces vendues')],
    ...view.buckets.map((b) => [bucketLabels(b.key, view.grain, lang).tip, int(b.a.pieces), money(b.a.valueCents), int(b.a.entriesQty), int(b.a.exitsQty),
      money(b.a.entriesValueCents), money(b.a.exitsValueCents), int(b.a.soldQty)]),
    [],
    [t('Âge du stock'), t('Stock pièces'), t('Stock valeur ({code})', { code }), view.age.basis === 'value' ? t('Part de la valeur (%)') : t('Part des pièces (%)'), t('Saisons')],
    ...view.age.buckets.map((b) => [t(AGE_FRAMES[b.age].label), int(b.pieces), money(b.valueCents), csvRate(b.shareBp), b.codes.map(shownValue).join(' · ')]),
    [],
    [fieldLabel(AXES.find((x) => x.v === axis)?.field ?? 'family'), t('Stock pièces'), t('Stock valeur ({code})', { code }), t('Pièces vendues'),
      t('Jours de vente en stock')],
    ...view.coverage.axes[axis].rows.map((r) => [shownValue(r.label), int(r.pieces), money(r.valueCents), int(r.soldQty), int(r.coverageDays)]),
    [],
    [fieldLabel('season'), t('Codes'), t('Pièces vendues'), t('Stock pièces'), t('Taux de sortie (%)'),
      ...view.sellThrough.weekStarts.map((w) => bucketLabels(w, 'week', lang).tip)],
    ...view.sellThrough.rows.map((r) => [rowSeasonName(r), r.codes.map(shownValue).join(' · '), int(r.soldQty), int(r.pieces), csvRate(r.sellThroughBp),
      ...r.weeks.map((v) => int(v))]),
    [t('Toutes saisons'), '', '', int(view.sellThrough.all.pieces), '', ...view.sellThrough.all.weeks.map((v) => int(v))],
    ...(sizes ? [
      [],
      [fieldLabel('sizeGrid'), fieldLabel('size'), t('Stock pièces'), t('Part du stock (%)'), t('Pièces vendues'), t('Part des ventes (%)'), t('Écart (points)')],
      ...sizes.rows.map((s) => [sizes.name, s.size, int(s.pieces), csvRate(s.stockShareBp), int(s.soldQty), csvRate(s.salesShareBp), int(s.gapPoints)]),
    ] : []),
    [],
    [t('Ce qui dort, rang'), t('Code'), t('Article'), t('Stock pièces'), t('Stock valeur ({code})', { code }), t('Pièces vendues'), t('Taux de sortie (%)'),
      t('Jours de vente en stock')],
    ...view.dormant.rows.map((d, i) => [String(i + 1), d.model, d.label, int(d.pieces), money(d.valueCents), int(d.soldQty), csvRate(d.sellThroughBp),
      int(d.coverageDays)]),
  ]);
}
